import { t } from '@lingui/macro'
import { format } from 'date-fns'
import { useFormikContext } from 'formik'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { mixpanel } from '@/mixpanel'
import {
  getMedicationDosageText,
  getMedicationPhotoFileIds,
  isMedicationFileProcessed,
} from '@/modules/app/utils/medication.utils'
import { useDeleteMedicationFile } from '@/modules/questionnaire/hooks/useDeleteMedicationFile'
import { useQuestionnaireTrackingProps } from '@/modules/questionnaire/hooks/useQuestionnaireTrackingProps'
import { useStoredMedicationFiles } from '@/modules/questionnaire/hooks/useStoredMedicationFiles'
import { useFeatureFlag } from '@/utils/feature-flag'
import { StructuredObservation } from '../structured-observation/StructuredObservation'
import { type ObservationAction, useDiffTrackedObservations } from '../useDiffTrackedObservations'
import { MedicationPhotoBadge } from './MedicationPhotoBadge'
import { MedicationPhotoFiles } from './MedicationPhotoFiles'
import { type IdentifiedMedicationStatement, MedicationContext } from './MedicationQuestion.context'
import { parseMedicationTimestamp, resolveDosageMode } from './MedicationQuestion.helpers'
import { MedicationStoredPhoto } from './MedicationStoredPhoto'
import { MedicationItemForm } from './medication-form/MedicationItemForm'

import type {
  MedicationFileAttachment,
  MedicationScheme,
  MedicationSchemeStatus,
  MedicationStatement,
} from '@/modules/app/types/HealthParameter.types'

// ================================ Types ================================

type MedicationQuestionProps = {
  questionId: string
  value?: MedicationScheme
  schemeStatus: MedicationSchemeStatus
  /** Mime types the question accepts, as configured in the backoffice. */
  mimeTypes?: string[]
  onSubmitAndAdvance: (value: MedicationScheme) => void
}

// ================================ Helpers ================================

const getDosageSubtitle = (medication: IdentifiedMedicationStatement): string | undefined => {
  return getMedicationDosageText(medication) || undefined
}

/**
 * A question offers photo capture once it accepts an image. The parser only
 * reads JPEG, but the patient's camera and library hand back other image types
 * that are converted before upload, so any image counts.
 */
const acceptsImages = (mimeTypes: string[] | undefined): boolean =>
  Boolean(mimeTypes?.some((mimeType) => mimeType.startsWith('image/')))

/** Mixpanel `source` property for a medication entry. */
const getTrackingSource = (medication: MedicationStatement): 'zorgi' | 'synops' | 'free_text' => {
  if (medication.code?.system === 'zorgi-article') return 'zorgi'
  if (medication.synopsId) return 'synops'
  return 'free_text'
}

// ================================ Component ================================

export const MedicationQuestion = ({
  questionId,
  value,
  schemeStatus: initialSchemeStatus,
  mimeTypes,
  onSubmitAndAdvance: submitAndAdvance,
}: MedicationQuestionProps) => {
  const theme = useTheme()
  const deleteFile = useDeleteMedicationFile(value)
  const trackingProps = useQuestionnaireTrackingProps()
  const photoParsingEnabled = Boolean(useFeatureFlag('medication_photo_parsing'))
  const [isPhotoInfoOpen, setPhotoInfoOpen] = useState(false)
  const { setFieldValue } = useFormikContext<Record<string, unknown>>()

  const trackEntrySaved = (action: ObservationAction, medication: MedicationStatement) => {
    mixpanel.track('medication_entry_saved', {
      action,
      source: getTrackingSource(medication),
      dosage_mode: resolveDosageMode(medication) === 'structured' ? 'structured' : 'free_text',
      ...trackingProps,
    })
  }

  const {
    value: scheme,
    identified: identifiedMedications,
    visible: visibleMedications,
    schemeStatus,
    view,
    addEntry: addMedication,
    editEntry: editMedication,
    removeEntry,
    confirmEmpty: confirmNoMedication,
    setView: handleSetView,
    onSubmitAndAdvance: handleSubmitAndAdvance,
  } = useDiffTrackedObservations<MedicationStatement, MedicationScheme>({
    questionId,
    listKey: 'medications',
    fallbackValue: value,
    initialSchemeStatus,
    hasOtherContent: (scheme) => Boolean(scheme.files?.length),
    // Fields kept from the existing entry on edit: the server-origin identity,
    // and the photo the line was read from, which the form does not hold.
    identityKeys: ['synopsId', 'source', 'status', 'attachment'],
    buildPrevious: (medication) => ({
      name: medication.name,
      dosage: medication.dosage,
    }),
    trackEntrySaved,
    // Recorded on every submit so the care team knows whether AI read the photos.
    submitAndAdvance: (value) => submitAndAdvance({ ...value, aiExtraction: photoParsingEnabled }),
  })

  const schemeFiles = scheme.files ?? []

  const storedFiles = useStoredMedicationFiles(getMedicationPhotoFileIds(scheme.medications ?? [], schemeFiles))

  /**
   * Keeps a file the parser could not read one medication from.
   *
   * The scheme counts as recorded once a file is on it: the patient has handed
   * something over, even though no medication line came out of it, and should be
   * able to move on.
   */
  const addSchemeFile = (file: MedicationFileAttachment) => {
    setFieldValue(questionId, {
      ...scheme,
      schemeStatus: 'recorded',
      medications: scheme.medications ?? [],
      files: [...schemeFiles, file],
    })
  }

  const fileProcessedText = t({
    id: 'medication.file.processed',
    comment: 'Shown in place of a medication file the care team processed and removed',
    message: 'Bestand verwerkt in je dossier',
  })

  const isProcessed = (fileId: string) => isMedicationFileProcessed(storedFiles.get(fileId))

  const photoAlt = t({
    id: 'medication.list.photoAlt',
    message: 'Foto van je medicatie',
  })

  const removeMedication = (id: string) => {
    const fileId = identifiedMedications.find((medication) => medication._clientId === id)?.attachment?.fileId
    if (fileId) {
      deleteFile(fileId)
    }

    removeEntry(id)
  }

  const removeSchemeFile = (fileId: string) => {
    deleteFile(fileId)
    setFieldValue(questionId, {
      ...scheme,
      medications: scheme.medications ?? [],
      files: schemeFiles.filter((file) => file.fileId !== fileId),
    })
  }

  // -- Render -----------------------------------------------------------------

  return (
    <MedicationContext.Provider
      value={{
        questionId,
        medications: identifiedMedications,
        schemeStatus,
        isPhotoCaptureEnabled: acceptsImages(mimeTypes),
        schemeFiles,
        addSchemeFile,
        removeSchemeFile,
        storedFiles,
        addMedication,
        editMedication,
        removeMedication,
        confirmNoMedication,
        setView: handleSetView,
        isPhotoInfoOpen,
        setPhotoInfoOpen,
        onSubmitAndAdvance: handleSubmitAndAdvance,
      }}
    >
      {view === 'choice' ? (
        <StructuredObservation.Choice
          questionId={questionId}
          emptyMessage={t({
            id: 'medication.emptyState',
            message: 'Nog geen medicatiegegevens gekend',
          })}
          addLabel={t({
            id: 'medication.choice.add',
            message: 'Ik wil medicatie toevoegen',
          })}
          noneLabel={t({
            id: 'medication.choice.none',
            message: 'Ik neem geen medicatie',
          })}
          onAdd={() => handleSetView('form')}
          onConfirmNone={confirmNoMedication}
        />
      ) : (
        <StructuredObservation.List
          items={visibleMedications}
          getId={(m) => m._clientId}
          getName={(m) => m.name}
          getSubtitle={getDosageSubtitle}
          // TODO-v10: passing rendered components into the list is the shape the
          // shared list forces today. Make the row a compound whose parts the
          // call site composes, then these go away.
          getBadge={(m) =>
            m.attachment && (
              <MedicationPhotoBadge>
                <MedicationStoredPhoto file={storedFiles.get(m.attachment.fileId)} alt={photoAlt} />
                {isProcessed(m.attachment.fileId) ? (
                  <MedicationPhotoBadge.Text>{fileProcessedText}</MedicationPhotoBadge.Text>
                ) : (
                  <MedicationPhotoBadge.Content>
                    <MedicationPhotoBadge.Icon icon={['fas', 'sparkle']} />
                    <MedicationPhotoBadge.Label>
                      {t({
                        id: 'medication.list.readFromPhoto',
                        message: 'Met AI uit je foto gelezen',
                      })}
                    </MedicationPhotoBadge.Label>
                  </MedicationPhotoBadge.Content>
                )}
              </MedicationPhotoBadge>
            )
          }
          attachments={
            schemeFiles.length > 0 && (
              <MedicationPhotoFiles>
                <MedicationPhotoFiles.Title>
                  {t({
                    id: 'medication.photoFiles.title',
                    message: "Foto's en bestanden",
                  })}
                </MedicationPhotoFiles.Title>

                {schemeFiles.map((photo) => {
                  // TODO-v10: TEMP
                  const takenAt = parseMedicationTimestamp(photo.takenAt)

                  return (
                    <MedicationPhotoFiles.Item key={photo.fileId}>
                      <MedicationStoredPhoto file={storedFiles.get(photo.fileId)} alt={photoAlt} />

                      <FlexColumn>
                        <MedicationPhotoFiles.Name>
                          {isProcessed(photo.fileId)
                            ? fileProcessedText
                            : t({
                                id: 'medication.photoFiles.name',
                                message: 'Foto van je medicatie',
                              })}
                        </MedicationPhotoFiles.Name>
                        {/* TODO-v10: TEMP */}
                        {takenAt && (
                          <MedicationPhotoFiles.Timestamp>
                            {format(takenAt, 'dd/MM/yyyy HH:mm')}
                          </MedicationPhotoFiles.Timestamp>
                        )}
                      </FlexColumn>

                      <IconButton
                        type='button'
                        size='large'
                        icon={['fal', 'trash-can']}
                        variant='Tertiary'
                        color={theme.Colors.Status.Error}
                        aria-label={t({
                          id: 'medication.file.remove',
                          comment: 'Label of the trash button that removes a medication file from the scheme',
                          message: 'Verwijder dit bestand',
                        })}
                        onClick={() => removeSchemeFile(photo.fileId)}
                      />
                    </MedicationPhotoFiles.Item>
                  )
                })}

                <MedicationPhotoFiles.Note>
                  {t({
                    id: 'medication.photoFiles.note',
                    message: 'Deze gaan mee naar je opnamedossier zodat je zorgteam deze kan verwerken.',
                  })}
                </MedicationPhotoFiles.Note>
              </MedicationPhotoFiles>
            )
          }
          onDelete={removeMedication}
          addLabel={t({
            id: 'medication.form.addButton',
            message: 'Medicatie toevoegen',
          })}
          onSubmitAndAdvance={handleSubmitAndAdvance}
          isSubmitDisabled={
            visibleMedications.length === 0 && schemeFiles.length === 0 && schemeStatus !== 'confirmed-empty'
          }
          emptyMessage={
            schemeStatus === 'confirmed-empty'
              ? t({
                  id: 'medication.emptyState.confirmed',
                  message: 'Je neemt geen medicatie',
                })
              : undefined
          }
        >
          <StructuredObservation.ItemDialogue
            // TODO-v10: the photo error screen should not show this title. Only the
            // form knows about that error, so let the form render its own heading:
            // make `title` optional here and move these strings into
            // MedicationItemForm's form screen. Needs regression testing on desktop,
            // where the heading leaves the row with the close button.
            back={
              isPhotoInfoOpen
                ? {
                    label: t({
                      id: 'medication.form.photo.info.back',
                      comment: 'Label of the button that leaves the photo info panel for the form',
                      message: 'Terug naar medicatie toevoegen',
                    }),
                    onBack: () => setPhotoInfoOpen(false),
                  }
                : undefined
            }
            title={(mode) => {
              if (isPhotoInfoOpen) {
                return t({
                  id: 'medication.form.photo.info.title',
                  message: 'Wat gebeurt er met mijn foto?',
                })
              }

              return mode === 'update'
                ? t({
                    id: 'medication.form.editTitle',
                    message: 'Medicatie bewerken',
                  })
                : t({
                    id: 'medication.form.addTitle',
                    message: 'Medicatie toevoegen',
                  })
            }}
          >
            <MedicationItemForm />
          </StructuredObservation.ItemDialogue>
        </StructuredObservation.List>
      )}
    </MedicationContext.Provider>
  )
}
